import { useDynamicAnimations } from 'assets/animations';
import type { AnimationItem, AnimationSegment } from 'lottie-web';
import Lottie from 'lottie-web';
import React from 'react';
import ReactDOM from 'react-dom';

import styles from '../styles.module.scss';

export interface OrderLoaderRef {
  triggerLoading(): any;
  triggerSuccess(id?: string): any;
  triggerFailure(): any;
}

interface Props {
  onAnimationComplete?(state: 'success' | 'failure', id?: string): any;
  show?: boolean;
}

const LOADER_SEGMENT: AnimationSegment = [0, 36];
const SUCCESS_SEGMENT: AnimationSegment = [37, 96];
const FAILURE_SEGMENT: AnimationSegment = [97, 157];

const OrderLoader = React.forwardRef<OrderLoaderRef, Props>(({ onAnimationComplete = () => {}, show = false }, ref) => {
  const { createOrderAnimation } = useDynamicAnimations();

  const rootEl = document && document.querySelector('#root');
  const element = React.useRef<HTMLDivElement>(null);
  const lottie = React.useRef<AnimationItem>();
  const orderIdRef = React.useRef<string>();
  const loaderResult = React.useRef<'success' | 'failure'>('success');

  const initLottie = React.useCallback(() => {
    if (!element.current) return;
    const anim = Lottie.loadAnimation({
      container: element.current, // the dom element that will contain the animation
      renderer: 'svg',
      loop: true,
      autoplay: false,
      animationData: createOrderAnimation,
    });
    anim.playSegments(LOADER_SEGMENT, true);
    lottie.current = anim;
  }, [createOrderAnimation]);

  const triggerLoading = () => {
    if (!lottie.current) return;
    lottie.current.play();
  };

  const triggerSuccess = (id: string) => {
    if (!lottie.current) return;

    lottie.current.playSegments(SUCCESS_SEGMENT, false);

    lottie.current.loop = false;
    orderIdRef.current = id;
    loaderResult.current = 'success';
  };

  const triggerFailure = () => {
    if (!lottie.current) return;
    lottie.current.playSegments(FAILURE_SEGMENT, false);
    lottie.current.loop = false;
    loaderResult.current = 'failure';
  };

  React.useImperativeHandle(ref, () => ({
    triggerLoading,
    triggerSuccess,
    triggerFailure,
  }));

  // initialize lottie and attach event listeners
  React.useEffect(() => {
    if (!show || !createOrderAnimation) return undefined;
    initLottie();
    lottie.current?.addEventListener('complete', () => {
      onAnimationComplete(loaderResult.current, orderIdRef.current);
    });
    return () => {
      lottie.current?.destroy();
    };
  }, [createOrderAnimation, initLottie, onAnimationComplete, show]);

  if (!rootEl || !show) {
    return null;
  }

  return ReactDOM.createPortal(
    <div className={styles.animationContainer}>
      <div className={styles.loaderElement} ref={element} />
    </div>,
    rootEl
  );
});

export default OrderLoader;
